import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { getBorrowedFunction } from './borrowing.js'
import { isIsolatedBorrowing } from './context-window.js'
import { describeChord } from './descriptions.js'
import { getDiatonicFunction, isDominantSeventhVariant } from './diatonic.js'
import { getFunctionLexiconId } from './function-lexicon-map.js'
import { isIIVIPattern } from './ii-V-I-detection.js'
import { formatModulationSuffix } from './modulation-detection.js'
import { EXPECTED_RESOLUTION_TARGETS, getSecondaryDominantFunction } from './secondary-dominant.js'
import { getTritoneSubFunction } from './tritone-sub.js'
import type { ChordAnalysis, ChordConfidence, ChordFunction, ProgressionContext } from './types.js'

/**
 * Optional analyzer context. Carries forward-compatible fields shared with
 * thread B (`previous`, `next`, `scaleSource`) plus style hints. All fields
 * are optional; an absent `context` reproduces v1 behavior exactly.
 */
export interface AnalysisContext {
  /**
   * The chord immediately preceding this one in the progression. Reserved for
   * future use (e.g., approach-chord patterns). No v1/v2 rule reads this field —
   * it is documented in the interface so callers can already pass it through
   * without a follow-up signature change.
   */
  previous?: Chord
  /** Reserved for future use (cadence detection, voice-leading). */
  next?: Chord
  /**
   * How the scale was determined. When `'auto-detected'`, diatonic labels are
   * downgraded from `'high'` to `'medium'` to signal that the scale itself is
   * a guess. `'user-set'` and `'unknown'` (the default) keep diatonic at
   * `'high'` — see the discussion in the spec for why `'unknown'` is optimistic.
   */
  scaleSource?: 'user-set' | 'auto-detected' | 'unknown'
  /**
   * Style hint that lets the analyzer apply idiom-specific rules.
   *
   * - `'blues'` — I7 / IV7 / V7 map to I / IV / V with blues descriptions.
   *   See `docs/superpowers/specs/2026-05-14-style-aware-blues.md`.
   * - `'jazz'` — tritone-sub detection (`subV/X`), ii-V-I pattern tagging,
   *   and jazz-flavored modal-interchange descriptions. See
   *   `docs/superpowers/specs/2026-05-14-style-aware-jazz.md`.
   */
  style?: 'blues' | 'jazz'
  /**
   * Multi-chord context window (thread B-v2). The chords surrounding the one
   * being analyzed, with `currentIndex` pointing at the current chord inside
   * `chords`. The analyzer reads a ±2 window for isolated-borrowing detection:
   * a lone parallel-mode chord with no surrounding borrowings reads as a
   * chromatic outlier and is downgraded; a parallel-mode chord with other
   * parallel-mode chords in the window stays at its default confidence.
   */
  progression?: { chords: Chord[]; currentIndex: number }
  /**
   * AI-engine corroboration signal (thread B-v2). When present, the analyzer
   * compares its own chord-function verdict against the AI's prediction and
   * shifts confidence one level on agreement (boost) or disagreement
   * (downgrade), as long as the AI's score clears the threshold. A `null`
   * prediction or a low score has no effect — the analyzer trusts its own
   * verdict when the AI declines to commit.
   */
  aiCorroboration?: {
    /** The AI engine's predicted chord function for this same chord. */
    predictedFunction: ChordFunction | null
    /** AI engine's confidence in its prediction (0-1). */
    aiScore: number
  }
  /**
   * Externally-supplied modulation target (thread B-v2). When set and the
   * current chord is diatonic to the candidate, the analyzer relabels against
   * the candidate scale and appends "(modulating to <key>)" to the description.
   * Detection of when modulation is happening is out of scope here — callers
   * (key trackers, AI engines) pass the candidate in; the analyzer only
   * decides whether to honor it for this chord.
   */
  modulationCandidate?: Scale
}

export interface AnalyzeChordInput {
  chord: Chord
  scale: Scale | undefined
  context?: AnalysisContext
}

/** AI corroboration boost/downgrade threshold. Below this the signal is ignored. */
const AI_CORROBORATION_THRESHOLD = 0.7

const CONFIDENCE_LADDER: ChordConfidence[] = ['low', 'medium', 'high']

/** Shift a confidence value up or down the ladder, clamped to the ends. */
const shiftConfidence = (value: ChordConfidence, delta: -1 | 1): ChordConfidence => {
  const index = CONFIDENCE_LADDER.indexOf(value)
  const next = Math.max(0, Math.min(CONFIDENCE_LADDER.length - 1, index + delta))
  return CONFIDENCE_LADDER[next]
}

/**
 * Apply the AI-corroboration rule. The analyzer's existing verdict is in
 * `analysis`; we may shift its `confidence` by one level depending on whether
 * the AI engine's prediction matches.
 *
 * Boost when AI agrees AND aiScore exceeds the threshold; downgrade when AI
 * predicts something different AND aiScore exceeds the threshold. Otherwise
 * pass through unchanged — a missing prediction or a low score is treated as
 * "no signal".
 */
const applyAiCorroboration = (
  analysis: ChordAnalysis,
  corroboration: AnalysisContext['aiCorroboration']
): ChordAnalysis => {
  if (!corroboration) {
    return analysis
  }
  const { predictedFunction, aiScore } = corroboration
  if (predictedFunction === null || aiScore <= AI_CORROBORATION_THRESHOLD) {
    return analysis
  }
  if (predictedFunction === analysis.chordFunction) {
    return { ...analysis, confidence: shiftConfidence(analysis.confidence, 1) }
  }
  return { ...analysis, confidence: shiftConfidence(analysis.confidence, -1) }
}

/**
 * Blues-specific description swap. When the analyzer returns a diatonic label
 * for a dom7 chord under `style: 'blues'`, override the description to one
 * that names the blues idiom. `V` keeps its standard dominant description —
 * the major-key V text already fits the blues V — so we only swap `I` and
 * `IV`. Non-dom7 chords and other functions fall through to `describeChord`.
 */
const bluesDom7Description = (chordFunction: ChordFunction): string | null => {
  if (chordFunction === 'I') {
    return 'Tonic — blues seven.'
  }
  if (chordFunction === 'IV') {
    return 'Subdominant — blues seven.'
  }
  return null
}

/**
 * Compute the confidence for a diatonic chord. `'high'` is the default; the
 * only downgrade is the explicit `'auto-detected'` scale-source signal.
 */
const diatonicConfidence = (context: AnalysisContext | undefined): ChordConfidence =>
  context?.scaleSource === 'auto-detected' ? 'medium' : 'high'

/**
 * Compute the confidence for a secondary-dominant chord. Defaults to
 * `'medium'`. When the caller provides `context.next`, we check whether the
 * following chord's diatonic function matches the expected resolution target
 * (`V/V → V`, `V/ii → ii`, …). A non-matching `next` drops confidence to
 * `'low'`. A missing or chromatic `next` keeps the default — the analyzer is
 * optimistic when information is unavailable.
 */
const secondaryDominantConfidence = (
  chordFunction: ChordFunction,
  scale: Scale,
  context: AnalysisContext | undefined
): ChordConfidence => {
  if (!context?.next) {
    return 'medium'
  }
  const expected = EXPECTED_RESOLUTION_TARGETS[chordFunction as keyof typeof EXPECTED_RESOLUTION_TARGETS]
  if (!expected) {
    return 'medium'
  }
  const nextFunction = getDiatonicFunction(context.next, scale)
  return nextFunction === expected ? 'medium' : 'low'
}

/**
 * Detect whether the current chord sits in a ii-V-I window. Returns the
 * progression tag when both neighbors are present and form ii → V → I in
 * `scale`; returns `undefined` otherwise so the caller can leave the field
 * unset on the analysis output.
 *
 * Only active under `style: 'jazz'`. Other styles return `undefined` to keep
 * the field absent on legacy callers' output. Whether the *current* chord is
 * the V (the conventional anchor) is enforced by the pattern check itself —
 * the window `(previous, current, next)` matches only when `current` is V.
 */
const jazzProgressionContext = (
  chord: Chord,
  scale: Scale,
  context: AnalysisContext | undefined
): ProgressionContext | undefined => {
  if (context?.style !== 'jazz') {
    return undefined
  }
  if (!context.previous || !context.next) {
    return undefined
  }
  return isIIVIPattern(context.previous, chord, context.next, scale) ? 'ii-V-I' : undefined
}

/**
 * Build the `progressionContext` field for the analysis result. Spread into
 * the returned `ChordAnalysis` so the field is *absent* (rather than
 * `undefined`) when no pattern matches — keeps the output shape clean for
 * non-jazz callers.
 */
const progressionContextField = (
  chord: Chord,
  scale: Scale,
  context: AnalysisContext | undefined
): { progressionContext: ProgressionContext } | Record<string, never> => {
  const tag = jazzProgressionContext(chord, scale, context)
  return tag ? { progressionContext: tag } : {}
}

/**
 * Compute the confidence for a parallel-mode borrowing. v1 default is
 * `'medium'`. v2 adds a context-window downgrade: if the caller supplied a
 * `progression` window and this borrowing is isolated (no other parallel-mode
 * chord in ±2 surrounding chords), drop to `'low'` — the lone-borrowing case
 * reads as a chromatic outlier rather than a consistent borrowing pattern.
 *
 * The check skips the current index itself — we already know the current
 * chord is a borrowing (that's why we're in this function).
 */
const borrowingConfidence = (scale: Scale, context: AnalysisContext | undefined): ChordConfidence => {
  if (!context?.progression) {
    return 'medium'
  }
  const { chords, currentIndex } = context.progression
  if (currentIndex < 0 || currentIndex >= chords.length) {
    return 'medium'
  }
  return isIsolatedBorrowing(chords, currentIndex, scale) ? 'low' : 'medium'
}

/**
 * Build the analysis for the current chord against the established scale. The
 * "no modulation candidate" path — the analyzer's normal v1 logic with the v2
 * borrowing-window refinement layered on top.
 */
const analyzeAgainstScale = (chord: Chord, scale: Scale, context: AnalysisContext | undefined): ChordAnalysis => {
  const isBlues = context?.style === 'blues'
  const isJazz = context?.style === 'jazz'
  const describeStyle = isJazz ? 'jazz' : undefined

  const diatonic = getDiatonicFunction(chord, scale, { allowDominantSevenths: isBlues })
  if (diatonic) {
    const bluesDescription = isBlues && isDominantSeventhVariant(chord) ? bluesDom7Description(diatonic) : null
    return {
      chordFunction: diatonic,
      borrowing: null,
      description: bluesDescription ?? describeChord({ chordFunction: diatonic, borrowing: null }, { style: describeStyle }),
      functionLexiconId: getFunctionLexiconId(diatonic, null),
      confidence: diatonicConfidence(context),
      ...progressionContextField(chord, scale, context),
    }
  }
  const borrowed = getBorrowedFunction(chord, scale)
  if (borrowed) {
    return {
      ...borrowed,
      description: describeChord(borrowed, { style: describeStyle }),
      functionLexiconId: getFunctionLexiconId(borrowed.chordFunction, borrowed.borrowing),
      confidence: borrowingConfidence(scale, context),
      ...progressionContextField(chord, scale, context),
    }
  }
  // Jazz: tritone substitution runs before secondary-dominant. A dom7 chord
  // a tritone above `next`'s root labels as `subV/<next>`. Requires `next` in
  // context — without it we fall through to secondary-dominant / chromatic.
  if (isJazz) {
    const subV = getTritoneSubFunction(chord, scale, context?.next)
    if (subV) {
      return {
        chordFunction: subV,
        borrowing: null,
        description: describeChord({ chordFunction: subV, borrowing: null }, { style: describeStyle }),
        functionLexiconId: getFunctionLexiconId(subV, null),
        // `next` is required for detection, and the spec specifies high
        // confidence in that case.
        confidence: 'high',
        ...progressionContextField(chord, scale, context),
      }
    }
  }
  const secondaryDominant = getSecondaryDominantFunction(chord, scale)
  if (secondaryDominant) {
    return {
      ...secondaryDominant,
      description: describeChord(secondaryDominant, { style: describeStyle }),
      functionLexiconId: getFunctionLexiconId(secondaryDominant.chordFunction, secondaryDominant.borrowing),
      confidence: secondaryDominantConfidence(secondaryDominant.chordFunction, scale, context),
      ...progressionContextField(chord, scale, context),
    }
  }
  return {
    chordFunction: 'chromatic',
    borrowing: null,
    description: describeChord({ chordFunction: 'chromatic', borrowing: null }, { style: describeStyle }),
    functionLexiconId: null,
    confidence: 'low',
    ...progressionContextField(chord, scale, context),
  }
}

/**
 * Try to relabel the chord against the modulation-candidate scale. Returns
 * the relabeled analysis (with `modulating: true` and a description suffix)
 * when the current chord is diatonic to the candidate; returns `null` when
 * the candidate doesn't accept the chord and the analyzer should fall back
 * to the original scale.
 *
 * v2 keeps the policy conservative: only a *diatonic* match in the candidate
 * key justifies the relabel. A chord that's only a borrowing in the candidate
 * key isn't enough signal to claim a modulation has happened.
 */
const analyzeAgainstModulationCandidate = (chord: Chord, candidate: Scale): ChordAnalysis | null => {
  const diatonic = getDiatonicFunction(chord, candidate)
  if (!diatonic) {
    return null
  }
  const baseDescription = describeChord({ chordFunction: diatonic, borrowing: null })
  return {
    chordFunction: diatonic,
    borrowing: null,
    description: `${baseDescription} ${formatModulationSuffix(candidate)}`,
    functionLexiconId: getFunctionLexiconId(diatonic, null),
    // Modulation hints are interpretive — the analyzer cannot prove the key
    // change from one chord. Cap at `'medium'` so consumers don't render a
    // false `'high'` chip while the modulation is still speculative.
    confidence: 'medium',
    modulating: true,
  }
}

/**
 * Analyze a chord against an established scale. Returns structured rationale
 * (function, borrowing, description, confidence) or null when no scale is set.
 *
 * The optional `context` parameter lets callers refine confidence — see
 * `AnalysisContext`. Callers that don't pass it get optimistic defaults:
 * diatonic → `'high'`, borrowing → `'medium'`, secondary dominant → `'medium'`,
 * chromatic → `'low'`.
 *
 * v2 ordering: modulation relabel runs first (it changes which scale we
 * interpret against). The normal scale-based analysis runs next. AI
 * corroboration is the final adjustment — it operates on the chord-function
 * label, whichever path produced it, so a modulating relabel can still be
 * confirmed or disconfirmed by the AI engine.
 */
export const analyzeChord = ({ chord, scale, context }: AnalyzeChordInput): ChordAnalysis | null => {
  if (!scale) {
    return null
  }
  const modulated =
    context?.modulationCandidate && analyzeAgainstModulationCandidate(chord, context.modulationCandidate)
  const base = modulated ?? analyzeAgainstScale(chord, scale, context)
  return applyAiCorroboration(base, context?.aiCorroboration)
}
